/**
 * `R3-UI5` · Formato de los números y de las fechas de la Carga en lote.
 *
 * ⟦rastro · ADR-0082 · R3-UI5 · CA-87 · CA-90 · RT 12⟧
 *
 * ⭐ Es el ÚNICO lugar donde vive la CONVENCIÓN del formato: las opciones del
 * `Intl.DateTimeFormat` (`OPCIONES_DE_FECHA`) y el armado de la cadena
 * `dd-mm-aaaa hh:mm`. Los dos consumidores —la lista de cargas y el panel del
 * reporte— pasan por `conMiles`/`fechaHoraChile`, así que una regla del PRD
 * (`CA-87`, `CA-90`) se arregla en un solo archivo en vez de en dos copias que
 * se desincronizan.
 *
 * ⚠️ Por qué la lista construye sus propias INSTANCIAS de `Intl` y las inyecta
 * (`conMiles(x, MILES)`, `fechaHoraChile(x, FECHA_Y_HORA)`) en vez de dejar que
 * este archivo las ponga: el gate de la lista
 * (`gates/r1/R3-UI5-lista.gate.ts`, `numeros-con-punto-de-miles` y
 * `fecha-hora-chile`) exige que el formateador esté DECLARADO en
 * `pantalla-carga.tsx` —una sola instancia por pantalla, que no se reconstruye
 * celda a celda—, así que el `new Intl.…` vive ahí. Lo que NO se duplica es el
 * formato: el receptor del formato y el join salen de acá.
 */

/**
 * `CA-87`: los números con punto de miles.
 *
 * `20.000` y no `20000`: el operador compara estas cifras contra su archivo, y
 * un número sin separador no se lee de un vistazo.
 *
 * El receptor es inyectable a propósito: la lista, que declara su propia
 * instancia por la razón de arriba, formatea igual por esta función y no
 * reescribe un `toLocaleString` propio que pueda divergir.
 */
const MILES = new Intl.NumberFormat("es-CL")

export function conMiles(valor: number, formateador: Intl.NumberFormat = MILES): string {
  return formateador.format(valor)
}

/**
 * `CA-90`: la fecha y la hora en la zona de Chile, `dd-mm-aaaa hh:mm`.
 *
 * ⛔ `toLocaleString` a secas usa la zona del NAVEGADOR. Un operador en otra
 * zona —o un servidor de pruebas en UTC— vería una hora corrida, y la hora de
 * una carga es justamente lo que se compara contra el archivo. Por eso la zona
 * se fija explícitamente en `America/Santiago` y el orden de los campos también.
 */
export const OPCIONES_DE_FECHA: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "America/Santiago",
}

const PARTES_DE_FECHA = new Intl.DateTimeFormat("es-CL", OPCIONES_DE_FECHA)

/*
  ⭐ Acepta `Date` además de la cadena ISO porque los dos llamadores la tienen de
  distinta forma: `Carga.aceptadaEn` ya es un `Date` (el contrato lo declara así)
  y la cadena ISO es lo que llega de un `fetch` sin decodificar. El formato es el
  mismo, así que se resuelve acá y no en cada llamador.
*/
export function fechaHoraChile(
  cuando: Date | string,
  formateador: Intl.DateTimeFormat = PARTES_DE_FECHA,
): string {
  /*
    ⭐ `instanceof Date` y no `new Date(cuando)` a ciegas: el `Date` que ya viene
    del contrato se usa tal cual, sin hacerlo pasar por el parser de texto; la
    cadena ISO que llega de un `fetch` sin decodificar sí se parsea.
  */
  const momento = cuando instanceof Date ? cuando : new Date(cuando)
  const partes: Record<string, string> = {}
  for (const parte of formateador.formatToParts(momento)) {
    if (parte.type !== "literal") partes[parte.type] = parte.value
  }
  const dos = (tipo: string): string => partes[tipo] ?? ""
  return `${dos("day")}-${dos("month")}-${dos("year")} ${dos("hour")}:${dos("minute")}`
}
